/**
 * Template Utilities
 * Helper functions for template operations
 */

/**
 * Extract variable numbers from text (e.g., "Hello {{1}} and {{2}}" -> [1, 2])
 */
export const extractVariableNumbers = (text) => {
  const matches = (text || "").match(/\{\{(\d+)\}\}/g) || [];
  return matches
    .map((match) => parseInt(match.replace(/[{}]/g, "")))
    .filter((num) => !isNaN(num) && num > 0)
    .sort((a, b) => a - b);
};

/**
 * Renumber variables sequentially (1, 2, 3...)
 * Returns the updated text and a map of old -> new numbers
 */
export const renumberVariables = (text, maxVariables = 10) => {
  const variableNumbers = extractVariableNumbers(text);
  const uniqueNumbers = [...new Set(variableNumbers)].slice(0, maxVariables);

  // Create mapping from old numbers to new sequential numbers
  const renumberMap = {};
  uniqueNumbers.forEach((oldNum, index) => {
    const newNum = index + 1;
    renumberMap[oldNum] = newNum;
  });

  // Check if renumbering is needed
  const needsRenumbering = Object.keys(renumberMap).some(
    (oldNum) => renumberMap[oldNum] !== parseInt(oldNum)
  );

  if (!needsRenumbering) {
    return { text, renumberMap: {} };
  }

  // Replace variables in reverse order to avoid conflicts
  let updatedText = text;
  Object.keys(renumberMap)
    .sort((a, b) => parseInt(b) - parseInt(a))
    .forEach((oldNum) => {
      const newNum = renumberMap[oldNum];
      if (oldNum !== newNum.toString()) {
        const regex = new RegExp(`\\{\\{${oldNum}\\}\\}`, "g");
        updatedText = updatedText.replace(regex, `{{${newNum}}}`);
      }
    });

  return { text: updatedText, renumberMap };
};

/**
 * Get next variable number to insert
 */
export const getNextVariableNumber = (text, maxVariables = 10) => {
  const existingNumbers = extractVariableNumbers(text);
  if (existingNumbers.length === 0) return 1;
  const maxNumber = Math.max(...existingNumbers);
  return Math.min(maxNumber + 1, maxVariables);
};

/**
 * Build components array from flat template data
 */
export const buildComponentsFromData = (data) => {
  const components = [];

  // HEADER component
  if (data.headerFormat && data.headerFormat !== "NONE") {
    const headerComponent = {
      type: "HEADER",
      format: data.headerFormat,
    };
    if (data.headerText) {
      headerComponent.text = data.headerText;
    }
    components.push(headerComponent);
  }

  // BODY component
  if (data.body) {
    components.push({
      type: "BODY",
      text: data.body,
      example: null,
    });
  }

  // FOOTER component
  if (data.footer) {
    components.push({
      type: "FOOTER",
      text: data.footer,
    });
  }

  // BUTTONS component
  if (data.buttons && data.buttons.length > 0) {
    components.push({
      type: "BUTTONS",
      buttons: data.buttons,
    });
  }

  return components;
};

/**
 * Extract variables from template JSON with samples
 */
export const extractVariablesFromTemplateJson = (templateJson) => {
  const variables = [];
  
  if (typeof templateJson === 'string') {
    try {
      templateJson = JSON.parse(templateJson);
    } catch (e) {
      return variables;
    }
  }

  const jsonComponents = templateJson.components || [];

  // Extract header variables
  const headerComponent = jsonComponents.find(c => c.type === 'HEADER');
  if (headerComponent && headerComponent.format === 'TEXT' && headerComponent.text) {
    const headerVarMatches = headerComponent.text.match(/\{\{(\d+)\}\}/g) || [];
    headerVarMatches.forEach((match, index) => {
      const varNum = match.replace(/[{}]/g, '');
      const sample = headerComponent.example?.header_text?.[index] || '';
      variables.push({
        id: Date.now() + index,
        name: varNum,
        sample: sample,
        target: 'header',
      });
    });
  }

  // Extract body variables
  const bodyComponent = jsonComponents.find(c => c.type === 'BODY');
  if (bodyComponent && bodyComponent.text) {
    const bodyVarMatches = bodyComponent.text.match(/\{\{(\d+)\}\}/g) || [];
    bodyVarMatches.forEach((match, index) => {
      const varNum = match.replace(/[{}]/g, '');
      const sample = bodyComponent.example?.body_text?.[0]?.[index] || 
                    bodyComponent.example?.body_text?.[index] || '';
      variables.push({
        id: Date.now() + 1000 + index,
        name: varNum,
        sample: sample,
        target: 'body',
      });
    });
  }

  return variables;
};

/**
 * Get WABA account from localStorage
 * @deprecated Use PhoneAccountContext instead. This function is kept for backward compatibility only.
 */
export const getWabaAccount = () => {
  console.warn("getWabaAccount() is deprecated. Use PhoneAccountContext.selectedPhoneAccount instead.");
  try {
    const savedWaba = localStorage.getItem("selectedWabaAccount");
    return savedWaba ? JSON.parse(savedWaba) : null;
  } catch (err) {
    console.error("Error parsing saved WABA account:", err);
    return null;
  }
};

/**
 * Build API payload from template data
 */
export const buildTemplatePayload = (templateData, wabaAccount) => {
  const components = templateData.components.map((component) => {
    const componentCopy = { ...component };

    // Add examples for BODY component
    if (component.type === "BODY") {
      const bodyVariables = templateData.variables
        .filter((v) => v.target === "body")
        .map((v) => v.sample);
      if (bodyVariables.length > 0) {
        componentCopy.example = {
          body_text: [bodyVariables],
        };
      }
    }

    // Add examples for HEADER component (TEXT format)
    if (component.type === "HEADER" && component.format === "TEXT") {
      const headerVariables = templateData.variables
        .filter((v) => v.target === "header")
        .map((v) => v.sample)
        .filter((sample) => sample && sample.trim() !== "");
      if (headerVariables.length > 0) {
        componentCopy.example = {
          header_text: headerVariables,
        };
      }
    }

    // Handle media headers
    if (
      component.type === "HEADER" &&
      ["IMAGE", "VIDEO", "DOCUMENT"].includes(component.format)
    ) {
      if (templateData.header_media_url) {
        componentCopy.example = {
          header_handle: [templateData.header_media_url],
        };
      } else {
        componentCopy.example = {
          header_handle: ["MEDIA_ID_PLACEHOLDER"],
        };
      }
    }

    // Clean up button objects
    if (component.type === "BUTTONS" && component.buttons) {
      componentCopy.buttons = component.buttons.map((btn) => {
        const buttonObj = {
          type: btn.type,
          text: btn.text,
        };

        if (btn.type === "URL") {
          if (btn.url) buttonObj.url = btn.url;
          if (btn.urlVariable && btn.urlVariable.sample) {
            buttonObj.example = [btn.urlVariable.sample];
          } else if (
            btn.example &&
            Array.isArray(btn.example) &&
            btn.example.length > 0
          ) {
            buttonObj.example = btn.example;
          }
        } else if (btn.type === "PHONE_NUMBER") {
          if (btn.phone_number) buttonObj.phone_number = btn.phone_number;
        }

        return buttonObj;
      });
    }

    return componentCopy;
  });

  return {
    name: templateData.name,
    language: templateData.language,
    category: templateData.category.toUpperCase(),
    components,
    waba_id: wabaAccount?.wabaId || templateData.wabaId,
    phone_id: wabaAccount?.phoneId || templateData.phoneId,
  };
};

